'use client';

import { c as _c } from "react/compiler-runtime";
import { useClearThesisReaction, useSetThesisReaction } from '@morfi/backend-sdk-v2/client';
import { m, useReducedMotion } from 'motion/react';
import { useLocale, useTranslations } from 'next-intl';
import type { MouseEvent } from 'react';
import type { ThesisItem } from '@/components/social/types';
import { AnimatedNumber } from '@/components/ui/animated-number';
import { Icon } from '@/components/ui/icon';
import { ArrowDown01IconSolid, ArrowUp01IconSolid } from '@/components/ui/icon/glyphs';
import { TOAST_IDS } from '@/config/toast-ids';
import { onEvent } from '@/events';
import { formatCountFor } from '@/lib/format/format-count';
import { logger } from '@/lib/telemetry/logger';
import { MOTION_TAP } from '@/lib/ui/easing';
import { showToast } from '@/lib/ui/toast';
import { cn } from '@/lib/ui/utils';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const REACTION_COUNT_ANIMATION_DURATION_MS = 300 as const;
const VOTE_ARROW_TAP = {
  scale: 0.97,
  transition: MOTION_TAP
} as const;
type Size = 'md' | 'sm';
const ARROW_SIZE_CLASS: Record<Size, string> = {
  md: 'size-6',
  sm: 'size-5'
};
const TAP_TARGET_SIZE_CLASS: Record<Size, string> = {
  md: 'h-6 w-6',
  sm: 'h-5 w-5'
};
const COUNTER_TEXT_CLASS: Record<Size, string> = {
  md: 'text-body',
  sm: 'text-caption'
};

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface ThesisVotePillProps {
  thesis: ThesisItem;
  isAuthenticated: boolean;
  onAuthRequired?: () => void;
  size?: Size;
  className?: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Single split-pill vote control. Left half = upvote, right half = downvote,
 * signed rating sits centered between them.
 *
 * SDK-authoritative (MOR-718): reaction + rating are read straight from the
 * (live) `thesis` row. Each mutation declares an optimistic delta helper
 * (`applyThesisReactionOptimistic`, targeting `useGetThesis` + `useGetTheses`),
 * so a click patches the SDK cache instantly and the settle-time refetch
 * reconciles it. The pill keeps NO local count/reaction state — the previous
 * local-optimistic copy stacked against the refetch (the 2 → 0 count drift) and
 * its in-flight flag wedged the buttons. The feed re-renders this row from cache
 * (`useLiveListData` at every call site), so reading the prop is live.
 */
export function ThesisVotePill(t0) {
  const $ = _c(55);
  const {
    thesis,
    isAuthenticated,
    onAuthRequired,
    size: t1,
    className
  } = t0;
  const size = t1 === undefined ? "md" : t1;
  const t = useTranslations("social.debate");
  const formatCount = formatCountFor(useLocale());
  const prefersReducedMotion = useReducedMotion();
  const setReactionMutation = useSetThesisReaction();
  const clearReactionMutation = useClearThesisReaction();
  const reaction = thesis.viewerReaction;
  const rating = thesis.rating;
  let t2;
  if ($[0] !== clearReactionMutation || $[1] !== isAuthenticated || $[2] !== onAuthRequired || $[3] !== reaction || $[4] !== setReactionMutation || $[5] !== t || $[6] !== thesis.signature) {
    t2 = function handleReact(next, e) {
      e.stopPropagation();
      e.preventDefault();
      if (!isAuthenticated) {
        onAuthRequired?.();
        return;
      }
      const clearing = next === reaction;
      const mutation = clearing ? clearReactionMutation.mutateAsync({
        signature: thesis.signature
      }) : setReactionMutation.mutateAsync({
        signature: thesis.signature,
        kind: next
      });
      mutation.then(() => {
        onEvent("social_thesis_reacted", {
          signature: thesis.signature,
          action: clearing ? "cleared" : next
        });
      }).catch(err => {
        if (err instanceof Error && "code" in err && (err as {
          code: string;
        }).code === "CONFLICT") {
          return;
        }
        logger.error("[ThesisVotePill] react failed", {
          error: err
        });
        showToast({
          id: TOAST_IDS.THESIS_REACTED,
          message: t("toast_reaction_failed"),
          variant: "error"
        });
      });
    };
    $[0] = clearReactionMutation;
    $[1] = isAuthenticated;
    $[2] = onAuthRequired;
    $[3] = reaction;
    $[4] = setReactionMutation;
    $[5] = t;
    $[6] = thesis.signature;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const handleReact = t2;
  const arrowSize = ARROW_SIZE_CLASS[size];
  const tapTargetSize = TAP_TARGET_SIZE_CLASS[size];
  const counterTextClass = COUNTER_TEXT_CLASS[size];
  let t3;
  if ($[8] !== counterTextClass) {
    t3 = cn("w-7 shrink-0 text-center font-medium tabular-nums text-foreground-tertiary", counterTextClass);
    $[8] = counterTextClass;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const counterClass = t3;
  let t4;
  if ($[10] !== className) {
    t4 = cn("inline-flex items-center gap-0.5 text-foreground-tertiary", className);
    $[10] = className;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== handleReact) {
    t5 = e_0 => handleReact("up", e_0);
    $[12] = handleReact;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== rating || $[15] !== t) {
    t6 = t("aria_thumbs_up", {
      count: rating
    });
    $[14] = rating;
    $[15] = t;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  const t7 = reaction === "up";
  let t8;
  if ($[17] !== tapTargetSize) {
    t8 = cn("inline-flex cursor-pointer items-center justify-center bg-transparent p-0", "hover:bg-transparent focus-visible:bg-transparent", tapTargetSize);
    $[17] = tapTargetSize;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== arrowSize || $[20] !== prefersReducedMotion || $[21] !== reaction) {
    t9 = prefersReducedMotion === true ? <Icon icon={ArrowUp01IconSolid} className={cn(arrowSize, reaction === "up" ? "fill-rating-good text-rating-good" : "text-foreground-tertiary")} /> : <m.span className="inline-flex origin-center" whileTap={VOTE_ARROW_TAP}><Icon icon={ArrowUp01IconSolid} className={cn(arrowSize, reaction === "up" ? "fill-rating-good text-rating-good" : "text-foreground-tertiary")} /></m.span>;
    $[19] = arrowSize;
    $[20] = prefersReducedMotion;
    $[21] = reaction;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== t5 || $[24] !== t6 || $[25] !== t7 || $[26] !== t8 || $[27] !== t9) {
    t10 = <button type="button" onClick={t5} aria-label={t6} aria-pressed={t7} className={t8}>{t9}</button>;
    $[23] = t5;
    $[24] = t6;
    $[25] = t7;
    $[26] = t8;
    $[27] = t9;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  const t11 = prefersReducedMotion === true ? <span>{formatCount(rating)}</span> : <AnimatedNumber value={rating} format={formatCount} duration={REACTION_COUNT_ANIMATION_DURATION_MS} />;
  let t12;
  if ($[29] !== counterClass || $[30] !== t11) {
    t12 = <output className={counterClass}>{t11}</output>;
    $[29] = counterClass;
    $[30] = t11;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  let t13;
  if ($[32] !== handleReact) {
    t13 = e_1 => handleReact("down", e_1);
    $[32] = handleReact;
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  let t14;
  if ($[34] !== rating || $[35] !== t) {
    t14 = t("aria_thumbs_down", {
      count: rating
    });
    $[34] = rating;
    $[35] = t;
    $[36] = t14;
  } else {
    t14 = $[36];
  }
  const t15 = reaction === "down";
  let t16;
  if ($[37] !== tapTargetSize) {
    t16 = cn("inline-flex cursor-pointer items-center justify-center bg-transparent p-0", "hover:bg-transparent focus-visible:bg-transparent", tapTargetSize);
    $[37] = tapTargetSize;
    $[38] = t16;
  } else {
    t16 = $[38];
  }
  let t17;
  if ($[39] !== arrowSize || $[40] !== prefersReducedMotion || $[41] !== reaction) {
    t17 = prefersReducedMotion === true ? <Icon icon={ArrowDown01IconSolid} className={cn(arrowSize, reaction === "down" ? "fill-rating-bad text-rating-bad" : "text-foreground-tertiary")} /> : <m.span className="inline-flex origin-center" whileTap={VOTE_ARROW_TAP}><Icon icon={ArrowDown01IconSolid} className={cn(arrowSize, reaction === "down" ? "fill-rating-bad text-rating-bad" : "text-foreground-tertiary")} /></m.span>;
    $[39] = arrowSize;
    $[40] = prefersReducedMotion;
    $[41] = reaction;
    $[42] = t17;
  } else {
    t17 = $[42];
  }
  let t18;
  if ($[43] !== t13 || $[44] !== t14 || $[45] !== t15 || $[46] !== t16 || $[47] !== t17) {
    t18 = <button type="button" onClick={t13} aria-label={t14} aria-pressed={t15} className={t16}>{t17}</button>;
    $[43] = t13;
    $[44] = t14;
    $[45] = t15;
    $[46] = t16;
    $[47] = t17;
    $[48] = t18;
  } else {
    t18 = $[48];
  }
  let t19;
  if ($[49] !== size || $[50] !== t10 || $[51] !== t12 || $[52] !== t18 || $[53] !== t4) {
    t19 = <div data-size={size} className={t4}>{t10}{t12}{t18}</div>;
    $[49] = size;
    $[50] = t10;
    $[51] = t12;
    $[52] = t18;
    $[53] = t4;
    $[54] = t19;
  } else {
    t19 = $[54];
  }
  return t19;
}